import React, { useState } from "react";
import { useSiteConfig } from "../../context/SiteConfigContext";
import contactAPI from "../../api/contactApi";
import { showSuccess, showError } from "../../utils/toastUtils";
import useAlphabetsInput from "../../hooks/useAlphabeticInput";
import useNumericInput from "../../hooks/useNumericInput";
import { Container, Row, Col, Form } from "react-bootstrap";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import {
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaYoutube,
  FaLinkedin,
} from "react-icons/fa";
import Breadcrump from "../../components/Breadcrump";

function ContactUs() {
  useAlphabetsInput();
  useNumericInput();
  const { config } = useSiteConfig();

  const INITAL_FORM_DATA = {
    email: "",
    phone: "",
    name: "",
    message: "",
  };

  const [formData, setFormData] = useState(INITAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please enter email address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone) {
      newErrors.phone = "Please enter phone";
    }

    if (!formData.name) {
      newErrors.name = "Please enter name";
    }

    if (!formData.message) {
      newErrors.message = "Please enter message";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const res = await contactAPI.store(formData);
      if (res.code === 200) {
        showSuccess(res.message || "Message sent successfully");
        setFormData(INITAL_FORM_DATA);
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  return (
    <>
      <Breadcrump title="Contact Us" />

      <div className="luxury-contact-page-wrapper">
        
        {/* EDITORIAL HERO SECTION */}
      
<section className="luxury-contact-hero py-5 text-center w-100">
  <Container>
    <Row className="justify-content-center">
      <Col md={10} lg={8} className="text-center d-flex flex-column align-items-center justify-content-center">
        
        {/* 🌟 Dynamic Clean Center Centered Tagline */}
        <span className="luxury-contact-subtitle text-center d-block w-100 mb-2">
          Get In Touch
        </span>
        
        {/* 🌟 Core Sub-heading Line Center Balanced */}
        <h1 className="luxury-contact-main-title text-center d-block w-100">
          We’d Love to Hear From You
        </h1>
        
        {/* 🌟 Description Paragraph Grid Block center constrained */}
        <p className="luxury-contact-hero-desc text-center mt-3 mb-0 mx-auto">
          For any questions or queries, whether you're already a customer or
          a potential customer, feel free to reach out to our friendly and
          knowledgeable customer support team.
        </p>

      </Col>
    </Row>
  </Container>
</section>

        {/* CORE DETAILS ROW SECTION */}
        <Container className="pb-5 mb-4">
          <div className="luxury-contact-card-box">
            <Row className="g-0 align-items-stretch">
              
              {/* LEFT COLUMN: VISUAL INFO CANVAS PANEL */}
              <Col lg={5} className="luxury-contact-info-panel p-4 p-md-5 d-flex flex-column justify-content-between text-white">
                <div>
                  <h3 className="info-panel-title mb-5">Contact Information</h3>
                  
                  <div className="luxury-info-item-block d-flex align-items-start mb-4">
                    <span className="info-icon-span"><Clock size={16} /></span>
                    <div className="info-text-side">
                      <h6>Support Timings</h6>
                      <p>Monday - Sunday (11:00 am - 8:30 pm)</p>
                    </div>
                  </div>

                  <div className="luxury-info-item-block d-flex align-items-start mb-4">
                    <span className="info-icon-span"><MapPin size={16} /></span>
                    <div className="info-text-side">
                      <h6>Address</h6>
                      <p>
                        {[
                          config?.address?.addressLine,
                          config?.address?.district,
                          config?.address?.state,
                        ].filter(Boolean).join(", ")}
                        {config?.address?.pincode ? ` - ${config?.address.pincode}` : ""}
                      </p>
                    </div>
                  </div>

                  <div className="luxury-info-item-block d-flex align-items-start mb-4">
                    <span className="info-icon-span"><Phone size={16} /></span>
                    <div className="info-text-side">
                      <h6>Phone Number</h6>
                      <a href={`tel:+91 ${config?.phone}`}>+91 {config?.phone}</a>
                      {config?.whatsappNumber && (
                        <div className="mt-1">
                          <a href={`https://wa.me/${config?.whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="whatsapp-link-text">
                            WhatsApp: +91 {config?.whatsappNumber}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="luxury-info-item-block d-flex align-items-start mb-4">
                    <span className="info-icon-span"><Mail size={16} /></span>
                    <div className="info-text-side">
                      <h6>Email Support</h6>
                      <a href={`mailto:${config?.email}`}>{config?.email}</a>
                    </div>
                  </div>
                </div>

                {/* Left Side Footer text & socials */}
                <div className="info-panel-footer-box pt-4">
                  <p className="holiday-disclaimer mb-4">
                    Please note that during public holidays, our response may take
                    longer than usual. We will always respond as soon as possible.
                  </p>
                  
                  <ul className="luxury-contact-socials-list d-flex gap-3 p-0 m-0">
                    {config?.socialMedia?.facebook && (
                      <li>
                        <a href={config?.socialMedia?.facebook} target="_blank" rel="noopener noreferrer">
                          <FaFacebook size={16} />
                        </a>
                      </li>
                    )}
                    {config?.socialMedia?.instagram && (
                      <li>
                        <a href={config?.socialMedia?.instagram} target="_blank" rel="noopener noreferrer">
                          <FaInstagram size={16} />
                        </a>
                      </li>
                    )}
                    {config?.socialMedia?.twitter && (
                      <li>
                        <a href={config?.socialMedia?.twitter} target="_blank" rel="noopener noreferrer">
                          <FaTwitter size={16} />
                        </a>
                      </li>
                    )}
                    {config?.socialMedia?.youtube && (
                      <li>
                        <a href={config?.socialMedia?.youtube} target="_blank" rel="noopener noreferrer">
                          <FaYoutube size={16} />
                        </a>
                      </li>
                    )}
                    {config?.socialMedia?.linkedin && (
                      <li>
                        <a href={config?.socialMedia?.linkedin} target="_blank" rel="noopener noreferrer">
                          <FaLinkedin size={16} />
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              </Col>

              {/* RIGHT COLUMN: REFINED MINIMAL FORM CANVAS PANEL */}
              <Col lg={7} className="luxury-contact-form-panel p-4 p-md-5 bg-white">
                <Form onSubmit={handleSubmit}>
                  <Row className="g-4">
                    
                    <div className="col-md-6">
                      <div className="contact-editorial-input-group">
                        <input
                          className="contact-editorial-field only-alphabets"
                          type="text"
                          name="name"
                          placeholder=" "
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                        <label className="contact-editorial-label">Your Name *</label>
                        {errors?.name && <span className="contact-field-error">{errors.name}</span>}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="contact-editorial-input-group">
                        <input
                          className="contact-editorial-field"
                          type="email"
                          name="email"
                          placeholder=" "
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                        <label className="contact-editorial-label">Email Address *</label>
                        {errors?.email && <span className="contact-field-error">{errors.email}</span>}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="contact-editorial-input-group">
                        <input
                          className="contact-editorial-field only-numeric"
                          type="text"
                          name="phone"
                          maxLength={10}
                          placeholder=" "
                          value={formData.phone}
                          onChange={handleChange}
                          required
                        />
                        <label className="contact-editorial-label">Phone Number *</label>
                        {errors?.phone && <span className="contact-field-error">{errors.phone}</span>}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="contact-editorial-input-group">
                        <textarea
                          className="contact-editorial-textarea"
                          rows={5}
                          name="message"
                          placeholder=" "
                          value={formData.message}
                          onChange={handleChange}
                          required
                        />
                        <label className="contact-editorial-label">Message *</label>
                        {errors?.message && <span className="contact-field-error">{errors.message}</span>}
                      </div>
                    </div>

                    <div className="col-12 mt-5">
                      <button type="submit" className="luxury-contact-submit-cta w-100">
                        SEND MESSAGE
                      </button>
                    </div>

                  </Row>
                </Form>
              </Col>

            </Row>
          </div>
        </Container>

        {/* MAP PANEL BLOCK */}
        {config?.address?.mapLocation && (
          <div className="luxury-google-map-wrapper w-100">
            <iframe
              src={config?.address?.mapLocation}
              className="luxury-embedded-iframe-map"
              style={{ border: "none" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Orangeapes Location Map"
            />
          </div>
        )}

      </div>
    </>
  );
}

export default ContactUs;